import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, Sun, Droplets, Waves, Loader2, Sparkles, Eye, Heart, Check, Minus, Plus, CreditCard } from "lucide-react";
import { MaskLine, Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";

const FLUORO_COLORS = [
  { name: "Neon Yellow", hex: "#E4F222" },
  { name: "Safety Orange", hex: "#FF6A00" },
  { name: "Fluorescent Coral", hex: "#FF6E79" },
  { name: "Hot Pink", hex: "#FF199C" },
  { name: "Electric Lime", hex: "#84E81B" },
  { name: "Bright Aqua", hex: "#14C6DD" },
  { name: "Neon Mint", hex: "#78E86A" },
  { name: "Fluorescent Blue", hex: "#1C9FF0" },
  { name: "Neon Violet", hex: "#B76BFF" },
];

const NEUTRAL_GIRLS = [
  { name: "Blush Pink", hex: "#F3DADD" },
  { name: "Dusty Rose", hex: "#C99AA0" },
  { name: "Ivory", hex: "#F4EFE5" },
  { name: "Sand", hex: "#E4D8C3" },
  { name: "Taupe", hex: "#B7A899" },
];

const NEUTRAL_BOYS = [
  { name: "White", hex: "#FFFFFF" },
  { name: "Stone", hex: "#D8D4CD" },
  { name: "Beige", hex: "#E7DBC3" },
  { name: "Olive", hex: "#9A9E7D" },
  { name: "Navy", hex: "#1B2A4A" },
];

const FLUORO_FEATURES = [
  { i: Eye, t: "High-visibility by design", d: "Bright fluorescent colors help a child stand out in and around the water — so little swimmers are easier to see and reach." },
  { i: Sun, t: "UPF 50+ protection", d: "Ultra-soft, quick-drying fabric that shields delicate skin through long days in the Florida sun." },
  { i: Droplets, t: "Made for the water", d: "Flat seams, tagless and chlorine-resistant for all-day comfort, in and out of the pool." },
  { i: ShieldCheck, t: "Discreet safety detail", d: "Child's name on the front and a parent's phone number tucked quietly inside the sleeve." },
];

const Swatch = ({ hex, name }) => (
  <div className="flex flex-col items-center gap-2" data-testid={`swatch-${name.toLowerCase().replace(/\s+/g, "-")}`}>
    <span
      className="w-11 h-11 rounded-full shadow-[0_6px_16px_rgba(11,27,61,0.18)]"
      style={{ backgroundColor: hex, border: hex === "#FFFFFF" ? "1px solid rgba(11,27,61,0.15)" : "none" }}
    />
    <span className="text-[11px] font-body tracking-wide text-center leading-tight" style={{ color: "var(--muted)" }}>{name}</span>
  </div>
);

const SIZES = ["6–12M", "12–18M", "18–24M", "2T", "3T", "4T", "5", "6", "7", "8"];

export default function Merch() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [collection, setCollection] = useState("Fluorescent");
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [childName, setChildName] = useState("");

  // Kickboard checkout
  const [kbQty, setKbQty] = useState(1);
  const [kbLoading, setKbLoading] = useState(false);

  const buyKickboard = async () => {
    setKbLoading(true);
    try {
      const { data } = await api.post("/payments/merch-checkout", {
        product: "kickboard",
        quantity: kbQty,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setKbLoading(false);
    }
  };

  const colorOptions =
    collection === "Fluorescent"
      ? FLUORO_COLORS
      : [...NEUTRAL_GIRLS, ...NEUTRAL_BOYS];

  const pickCollection = (c) => {
    setCollection(c);
    setColor("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", {
        email,
        collection,
        color: color || undefined,
        size: size || undefined,
        child_name: childName || undefined,
      });
      setJoined(true);
      toast.success("You're on the list! We'll let you know the moment rash guards drop.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="merch-page">
      {/* HERO */}
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div className="noise" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="text-center max-w-3xl mx-auto">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="overline mb-6 inline-flex items-center gap-2" style={{ color: "var(--gold)" }}>
              <Sparkles size={14} /> Introducing
            </motion.p>
            <h1 className="font-display leading-[1.02] tracking-tight text-5xl md:text-7xl" style={{ color: "var(--navy)" }}>
              <MaskLine>Mimi &amp; Me</MaskLine>
              <MaskLine delay={0.12} className="italic">rash guards.</MaskLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }} className="font-body font-light text-lg md:text-xl mt-8 leading-relaxed" style={{ color: "var(--muted)" }}>
              Personalized rash guards made for little swimmers — designed with the same love, care and
              safety-first thinking as every lesson. <span className="italic">Safety. Comfort. Confidence.</span>
            </motion.p>
          </div>
        </div>
      </section>

      {/* FLUORESCENT — WATER-SAFETY ESSENTIAL */}
      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="rounded-[2.5rem] overflow-hidden relative" style={{ backgroundColor: "var(--navy)" }}>
              <div className="noise opacity-40" />
              <div className="grid lg:grid-cols-2 gap-0 relative">
                {/* Copy */}
                <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-2 self-start px-4 py-2 rounded-full mb-6" style={{ backgroundColor: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.4)" }}>
                    <ShieldCheck size={15} style={{ color: "var(--gold)" }} />
                    <span className="overline" style={{ color: "var(--gold)" }}>A water-safety essential</span>
                  </span>
                  <h2 className="font-display text-4xl md:text-5xl leading-[1.05] text-white">
                    Brighter colors.<br /><span className="italic" style={{ color: "var(--gold)" }}>Safer swimmers.</span>
                  </h2>
                  <p className="font-body font-light text-base md:text-lg mt-6 leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>
                    Childhood drowning is rising — and it's still a leading cause of accidental death for
                    children ages 1–4. In the water, every second counts. High-visibility fluorescent
                    rash guards make a little swimmer far easier to spot beneath the surface and across a
                    busy pool, so a caring eye can find them faster.
                  </p>
                  <p className="font-display text-xl md:text-2xl italic mt-6" style={{ color: "var(--gold)" }}>
                    Because one second can make all the difference.
                  </p>
                  <p className="font-body font-light text-sm mt-4" style={{ color: "rgba(255,255,255,0.7)" }}>
                    We consider these part of the team — a simple, must-have layer of protection we
                    recommend for every child who swims with us.
                  </p>
                </div>
                {/* Flyer image */}
                <div className="relative min-h-[420px] lg:min-h-full">
                  <img
                    src={IMAGES.merchFluorescent}
                    alt="Mimi & Me fluorescent rash guards for children"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    data-testid="fluorescent-flyer"
                  />
                </div>
              </div>
            </div>
          </Reveal>

          {/* Fluorescent swatches */}
          <Reveal delay={0.1}>
            <div className="mt-10">
              <p className="overline text-center mb-6" style={{ color: "var(--gold)" }}>Available in nine high-visibility colors</p>
              <div className="flex flex-wrap justify-center gap-6 md:gap-9">
                {FLUORO_COLORS.map((c) => <Swatch key={c.name} {...c} />)}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY IT MATTERS — features */}
      <section className="py-16" style={{ backgroundColor: "var(--sand)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>Not just a shirt — peace of mind</p>
            <h2 className="font-display text-4xl md:text-5xl mb-12" style={{ color: "var(--navy)" }}>Made with love. Made for life.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FLUORO_FEATURES.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="p-7 rounded-2xl bg-white border h-full" style={{ borderColor: "rgba(212,175,55,0.25)" }}>
                  <p.i size={28} style={{ color: "var(--gold)" }} />
                  <h3 className="font-display text-2xl mt-4" style={{ color: "var(--navy)" }}>{p.t}</h3>
                  <p className="font-body font-light text-sm mt-2 leading-relaxed" style={{ color: "var(--muted)" }}>{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEUTRAL COLLECTION */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div className="rounded-3xl overflow-hidden border" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                <img src={IMAGES.merchNeutral} alt="Mimi & Me neutral rash guards for children" className="w-full h-full object-cover" data-testid="neutral-flyer" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <span className="inline-flex items-center gap-2 mb-5">
                  <Heart size={15} style={{ color: "var(--gold)" }} />
                  <span className="overline" style={{ color: "var(--gold)" }}>Discreet safety. Timeless style.</span>
                </span>
                <h2 className="font-display text-4xl md:text-5xl leading-[1.05]" style={{ color: "var(--navy)" }}>
                  The neutral <span className="italic">collection.</span>
                </h2>
                <p className="font-body font-light text-base md:text-lg mt-6 leading-relaxed" style={{ color: "var(--muted)" }}>
                  For families who love a softer, elevated look. Same premium UPF 50+ fabric and the same
                  discreet safety details — child's name on the front, parent's phone tucked inside the
                  sleeve — in a palette that pairs with everything.
                </p>

                <div className="mt-8">
                  <p className="overline mb-4" style={{ color: "var(--navy)" }}>For girls</p>
                  <div className="flex flex-wrap gap-5">
                    {NEUTRAL_GIRLS.map((c) => <Swatch key={c.name} {...c} />)}
                  </div>
                </div>
                <div className="mt-7">
                  <p className="overline mb-4" style={{ color: "var(--navy)" }}>For boys</p>
                  <div className="flex flex-wrap gap-5">
                    {NEUTRAL_BOYS.map((c) => <Swatch key={c.name} {...c} />)}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SWIM GEAR — KICKBOARD */}
      <section className="py-16" style={{ backgroundColor: "var(--sand)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline mb-4" style={{ color: "var(--gold)" }}>Swim gear</p>
            <h2 className="font-display text-4xl md:text-5xl mb-12" style={{ color: "var(--navy)" }}>Coach-favorite essentials</h2>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div className="rounded-3xl overflow-hidden border bg-white flex items-center justify-center p-6" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
                <img src={IMAGES.merchKickboard} alt="Junior Swimming Kickboard" className="w-full max-w-sm h-auto object-contain" data-testid="kickboard-image" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="kickboard-card">
                <span className="overline" style={{ color: "var(--gold)" }}>For kick &amp; core</span>
                <h3 className="font-display text-3xl md:text-4xl mt-3" style={{ color: "var(--navy)" }}>Junior Swimming Kickboard</h3>
                <p className="font-display text-3xl mt-3" style={{ color: "var(--gold)" }} data-testid="kickboard-price">$34.99</p>
                <p className="font-body font-light text-base md:text-lg mt-5 leading-relaxed" style={{ color: "var(--muted)" }}>
                  A little swimmer's best friend for building strong kicks, core stability and body
                  position. Lightweight and easy to hold, it lets your child focus on technique with
                  confidence — a favorite tool in every lesson.
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-6">
                  {["High-density EVA foam", "Ultra-buoyant construction", "Chlorine resistant", "Smooth rounded edges for grip"].map((f) => (
                    <li key={f} className="flex items-center gap-2 font-body text-sm" style={{ color: "var(--navy)" }}>
                      <Check size={16} style={{ color: "var(--gold)" }} /> {f}
                    </li>
                  ))}
                </ul>
                <p className="font-body text-sm mt-6 flex items-center gap-2" style={{ color: "var(--muted)" }}>
                  <Waves size={15} style={{ color: "var(--gold)" }} />
                  Handed off at your lessons in Boca Raton &amp; Palm Beach — no shipping.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-3 rounded-full border px-2 py-1.5" style={{ borderColor: "rgba(11,27,61,0.2)" }} data-testid="kickboard-qty">
                    <button type="button" onClick={() => setKbQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-[var(--sand)]" aria-label="Decrease quantity" data-testid="kickboard-qty-minus" style={{ color: "var(--navy)" }}>
                      <Minus size={16} />
                    </button>
                    <span className="font-display text-xl w-6 text-center" style={{ color: "var(--navy)" }} data-testid="kickboard-qty-value">{kbQty}</span>
                    <button type="button" onClick={() => setKbQty((q) => Math.min(10, q + 1))} className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-[var(--sand)]" aria-label="Increase quantity" data-testid="kickboard-qty-plus" style={{ color: "var(--navy)" }}>
                      <Plus size={16} />
                    </button>
                  </div>
                  <button onClick={buyKickboard} disabled={kbLoading} className="btn-primary" data-testid="kickboard-buy-btn">
                    {kbLoading ? <Loader2 size={18} className="animate-spin" /> : <><CreditCard size={16} /> Buy Now · ${(34.99 * kbQty).toFixed(2)}</>}
                  </button>
                </div>
                <p className="font-body text-xs mt-3" style={{ color: "var(--muted)" }}>Secure checkout · picked up at your lesson</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="py-24" style={{ backgroundColor: "var(--sand)" }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <Reveal>
            <Waves size={30} className="mx-auto mb-4" style={{ color: "var(--gold)" }} />
            <h2 className="font-display text-3xl md:text-5xl leading-tight" style={{ color: "var(--navy)" }}>
              Be first to <span className="italic">reserve yours.</span>
            </h2>
            <p className="font-body font-light text-lg mt-4" style={{ color: "var(--muted)" }}>
              Tell us your preferred collection, color and size, and we'll email you the moment rash
              guards are ready to order — <span className="italic">fluorescent safety colors first.</span>
            </p>
            {joined ? (
              <p className="font-display text-2xl mt-8" style={{ color: "var(--gold)" }} data-testid="waitlist-success">You're on the list! 🌊</p>
            ) : (
              <form onSubmit={submit} className="mt-10 text-left" data-testid="waitlist-form">
                {/* Collection */}
                <p className="overline mb-3" style={{ color: "var(--navy)" }}>Collection</p>
                <div className="flex gap-3 mb-7">
                  {["Fluorescent", "Neutral"].map((c) => (
                    <button
                      key={c} type="button" onClick={() => pickCollection(c)}
                      className="px-6 py-3 rounded-full font-body text-sm transition-colors border"
                      style={{
                        backgroundColor: collection === c ? "var(--navy)" : "#fff",
                        color: collection === c ? "#fff" : "var(--navy)",
                        borderColor: collection === c ? "var(--navy)" : "rgba(11,27,61,0.2)",
                      }}
                      data-testid={`collection-${c.toLowerCase()}`}
                    >
                      {c === "Fluorescent" ? "Fluorescent (safety)" : "Neutral"}
                    </button>
                  ))}
                </div>

                {/* Color */}
                <p className="overline mb-3" style={{ color: "var(--navy)" }}>Color <span style={{ color: "var(--muted)" }}>(optional)</span></p>
                <div className="flex flex-wrap gap-3 mb-7">
                  {colorOptions.map((c) => (
                    <button
                      key={c.name} type="button" onClick={() => setColor(color === c.name ? "" : c.name)}
                      title={c.name}
                      className="flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full border transition-colors"
                      style={{
                        borderColor: color === c.name ? "var(--gold)" : "rgba(11,27,61,0.15)",
                        backgroundColor: color === c.name ? "rgba(212,175,55,0.12)" : "#fff",
                      }}
                      data-testid={`color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="w-6 h-6 rounded-full" style={{ backgroundColor: c.hex, border: c.hex === "#FFFFFF" ? "1px solid rgba(11,27,61,0.15)" : "none" }} />
                      <span className="font-body text-xs" style={{ color: "var(--navy)" }}>{c.name}</span>
                    </button>
                  ))}
                </div>

                {/* Size + Child name */}
                <div className="grid sm:grid-cols-2 gap-4 mb-7">
                  <div>
                    <p className="overline mb-3" style={{ color: "var(--navy)" }}>Size <span style={{ color: "var(--muted)" }}>(optional)</span></p>
                    <select
                      value={size} onChange={(e) => setSize(e.target.value)}
                      className="w-full rounded-full bg-white px-5 py-3.5 font-body text-sm outline-none border"
                      style={{ borderColor: "rgba(11,27,61,0.2)", color: "var(--navy)" }}
                      data-testid="waitlist-size"
                    >
                      <option value="">Select a size</option>
                      {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="overline mb-3" style={{ color: "var(--navy)" }}>Child's name <span style={{ color: "var(--muted)" }}>(optional)</span></p>
                    <input
                      type="text" value={childName} onChange={(e) => setChildName(e.target.value)}
                      placeholder="For personalization"
                      className="w-full rounded-full bg-white px-5 py-3.5 font-body text-sm outline-none border"
                      style={{ borderColor: "rgba(11,27,61,0.2)", color: "var(--navy)" }}
                      data-testid="waitlist-childname"
                    />
                  </div>
                </div>

                {/* Email + submit */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 rounded-full bg-white px-6 py-4 font-body text-sm outline-none border"
                    style={{ borderColor: "rgba(11,27,61,0.2)", color: "var(--navy)" }}
                    data-testid="waitlist-email"
                  />
                  <button type="submit" disabled={loading} className="btn-primary" data-testid="waitlist-submit">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : "Reserve My Spot"}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
